/**
 * CategoryGroupDialog Component
 * 
 * Dialog for creating and editing category groups with form fields for name.
 */

import React, { ReactElement } from "react";
import {
  TextField,
  Alert,
  Typography,
  Box,
} from "@mui/material";
import StandardDialog from "../../../components/StandardDialog";
import { TransactionCategoryGroup } from "../../../types";

interface CategoryGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedTab: "expense" | "income";
  editingGroup: TransactionCategoryGroup | null;
  groups: TransactionCategoryGroup[];
  groupName: string;
  setGroupName: (name: string) => void;
  groupNameError: string;
  error: string;
  saving: boolean;
  creatingGroup: boolean;
  countCategoriesInGroup: (groupId: string) => number;
  countTransactionsInGroup: (groupId: string) => number;
}

/**
 * Dialog for adding or editing a category group
 */
function CategoryGroupDialog({
  open,
  onClose,
  onConfirm,
  selectedTab,
  editingGroup,
  groups,
  groupName,
  setGroupName,
  groupNameError,
  error,
  saving,
  creatingGroup,
  countCategoriesInGroup,
  countTransactionsInGroup,
}: CategoryGroupDialogProps): ReactElement {
  const isEditMode = !!editingGroup;
  const isLoading = saving || creatingGroup;

  const handleNameChange = (value: string): void => {
    setGroupName(value);
    // Duplicate checking is handled by parent component
  };

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={isEditMode ? "Edit Group" : `Create ${selectedTab === "expense" ? "Expense" : "Income"} Group`}
      confirmText={isLoading ? (isEditMode ? "Saving..." : "Creating...") : (isEditMode ? "Save" : "Create")}
      onConfirm={onConfirm}
      confirmDisabled={isLoading || !groupName.trim() || !!groupNameError}
      maxWidth="sm"
      positionAtTopOnMobile
    >
      <TextField
        autoFocus
        margin="dense"
        label="Group Name"
        fullWidth
        variant="outlined"
        value={groupName}
        onChange={(e) => handleNameChange(e.target.value)}
        disabled={isLoading}
        required
        error={!!groupNameError}
        helperText={groupNameError}
        onKeyPress={(e) => {
          if (e.key === "Enter" && !isLoading && groupName.trim() && !groupNameError) {
            onConfirm();
          }
        }}
      />
      
      {isEditMode && editingGroup && (() => {
        const categoryCount = countCategoriesInGroup(editingGroup.id);
        const transactionCount = countTransactionsInGroup(editingGroup.id);
        if (categoryCount > 0 || transactionCount > 0) {
          return (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" component="div">
                <strong>Warning:</strong> This group is currently in use:
                {categoryCount > 0 && (
                  <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                    • {categoryCount} categor{categoryCount === 1 ? "y uses" : "ies use"} this group
                  </Box>
                )}
                {transactionCount > 0 && (
                  <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                    • {transactionCount} transaction{transactionCount === 1 ? "" : "s"} use categor{transactionCount === 1 ? "y" : "ies"} in this group
                  </Box>
                )}
                <Box component="span" sx={{ display: "block", mt: 1, fontWeight: "medium" }}>
                  After changing the group name, all categories and transactions will be updated automatically.
                </Box>
              </Typography>
            </Alert>
          );
        }
        return null;
      })()}
      
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
      )}
    </StandardDialog>
  );
}

export default CategoryGroupDialog;
